import React, { useState, useContext, useEffect } from "react";
import { ThemeContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";

import { useHistory, Redirect } from "react-router-dom";

import Col from "react-bootstrap/Col";

import Sallie from "../Sallie";
import theme from "../../index.scss";
import Spinner from "../../Components/LoadingSpinner";
import ComponentWrapper from "./ComponentWrapper";
import PillButton from "../StyledComponents/PillButton";

import Check from "../../images/checkWithinCircle.svg";

import { sendReferral } from "../../api/referralRequest";

const ReferPatientSelection = ({
  user,
  patientList,
  caregiverList,
  setInputVisible,
}) => {
  const history = useHistory();
  const { dark } = useContext(ThemeContext);
  const { t } = useTranslation();

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isInputEmpty, setIsInputEmpty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({
    content: "",
    type: "",
  });

  useEffect(() => {
    if (
      !(
        user.userType == "caregiver" ||
        user.userType == "provider"
      )
    )
      history.push("/");
  }, []);

  if (!user) {
    return (
      <Redirect
        to={{
          pathname: "/signIn",
        }}
      />
    );
  }

  const createItemList = (res, caregiverList) =>
    [...new Set(caregiverList?.map(({ caregiverName }) => caregiverName))].map(
      (caregiver) => ({
        caregiver,
        items: res.filter((item) => item.caregiverName === caregiver),
      })
    );

  const caregiverPatientList = createItemList(caregiverList, caregiverList);

  const PatientItem = ({ patient }) => {
    const [email, setEmail] = useState("");
    const handleButtonClick = () => {
      if (selectedPatient?.patientId === patient.patientId) {
        setSelectedPatient(null);
      } else {
        setSelectedPatient(patient);
      }
    };

    const handleSubmit = async () => {
      setSubmitting(true);

      if (!email) {
        setIsInputEmpty(true);
        setSubmitting(false);
        return;
      }

      try {
        const updatedPatient = {
          ...user,
          ...selectedPatient,
          recipientEmail: email.toLowerCase(),
        };
        await sendReferral(updatedPatient);
        setIsInputEmpty(false);
        setMessage({ content: t("dashboard.referPatientSelection.messages.sent"), type: "success" });
      } catch (error) {
        console.error(error);
        if (error.response && error.response.status === 404) {
          const handleInviteProvider = () => {
            setInputVisible(true);
            history.push("/", {
              fromReferPatient: true,
            });
          };

          setMessage({
            content: (
              <span>
                {t("dashboard.referPatientSelection.messages.noEmail.0")}
                <a href="#" onClick={handleInviteProvider}>
                  {t("dashboard.referPatientSelection.messages.noEmail.1")}
                </a>
                .
              </span>
            ),
            type: "error",
          });
        } else if (error.response && error.response.status === 402) {
          setMessage({
            content: t("dashboard.referPatientSelection.messages.accountType"),
            type: "error",
          });
        } else if (error.response && error.response.status === 401) {
          setMessage({ content: error.response.data.message, type: "error" });
        } else if (error.response && error.response.status === 500) {
          setMessage({
            content: t("dashboard.referPatientSelection.messages.fail"),
            type: "error",
          });
        } else {
          setMessage({
            content: t("dashboard.referPatientSelection.messages.error"),
            type: "error",
          });
        }
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div
        key={patient.patientId}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <PillButton
          autoMargin
          onClick={handleButtonClick}
          variant={
            selectedPatient?.patientId === patient.patientId
              ? "info"
              : "secondary"
          }
        >
          {selectedPatient?.patientId === patient.patientId && (
            <img alt={t("dashboard.ui.checked")} src={Check} />
          )}
          {`(${patient.firstName} ${patient.lastName}`}{" "}
          {patient.externalId && `ID: ${patient.externalId}`})
        </PillButton>
        {selectedPatient?.patientId === patient.patientId && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              marginTop: "1rem",
              marginBottom: "1rem",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "start",
              }}
            >
              <span style={{ display: "inline" }}>{t("dashboard.referPatientSelection.receiverEmail")}</span>
              <span className="required">*</span>
            </div>
            {isInputEmpty && (
              <div style={{ color: "red" }}>{t("dashboard.referPatientSelection.enterEmail")}</div>
            )}
            {message.type == "error" && (
              <div className="required">{message.content}</div>
            )}
            {message.type == "success" && (
              <div style={{ color: "green" }}>{message.content}</div>
            )}
            <input
              type="email"
              style={{ width: "18rem" }}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {submitting ? (
              <Spinner />
            ) : (
              <button style={{ width: "18rem" }} onClick={handleSubmit}>
                {t("dashboard.referPatientSelection.sendRequest")}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <Sallie text={t("dashboard.referPatientSelection.sallie")} />
      <Col
        className={`d-flex flex-wrap align-items-end ${"justify-content-end"}`}
      >
        <h6
          className="pt-2 pb-2 mb-4"
          style={{
            width: "30%",
            borderRadius: "20px",
            backgroundColor: dark ? theme.darkModePrimary : "#3EB1A6",
            color: dark ? theme.darkModeText : "white",
            textAlign: "center",
            overflowWrap: "break-word",
            cursor: "pointer",
          }}
          onClick={() => history.push("/patientControl")}
        >
          {t("dashboard.referPatientSelection.switchPatient")}
        </h6>
      </Col>
      <div className="w-75 p-3 mx-auto">
        <ComponentWrapper>
          <h6 className="d-block">
            {patientList && patientList.length === 0
              ? t("dashboard.referPatientSelection.noPatient")
              : t("dashboard.referPatientSelection.choosePatient")}
          </h6>
          {patientList && <h5>{t("dashboard.referPatientSelection.patientList")}</h5>}
          {/* // <Spinner animation="border" role="status">
                        //   <span className="sr-only">Loading...</span>
                        // </Spinner> */}
          {patientList && (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {patientList
                .sort((a, b) => a?.firstName?.localeCompare(b?.firstName))
                .map((patient, index) => (
                  <PatientItem key={patient.patientId} patient={patient} />
                ))}
            </div>
          )}
          {user.userType != "caregiver" && caregiverList && (
            <h5>{t("dashboard.referPatientSelection.caregiverList")}</h5>
          )}
          {user.userType != "caregiver" &&
            caregiverList && //for provider
            caregiverPatientList.map((arr, index1) => {
              return (
                <div key={index1}>
                  <h6> {arr.caregiver}</h6>
                  {arr.items
                    .sort((a, b) => a?.firstName?.localeCompare(b?.firstName))
                    .map((patient, index) => (
                      <PatientItem key={patient.patientId} patient={patient} />
                    ))}
                </div>
              );
            })}
        </ComponentWrapper>
      </div>
    </div>
  );
};

export default ReferPatientSelection;